import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { BOOK_DEMO_URL } from "../lib/constants";
import { CONTACT_PHONE, CONTACT_PHONE_TEL } from "../lib/siteConfig";
import logo from "../assets/high-resolution-color-logo.png";

const PRODUCTS = [
  { label: "Attack Surface", desc: "External asset discovery", to: "/asm" },
  { label: "Digital Risk", desc: "Dark web monitoring", to: "/dri" },
  { label: "Compliance", desc: "Automated frameworks", to: "/compliance" },
];

function ProductsDropdown() {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary dark:hover:text-primary-fixed-dim transition-colors py-2">
        Products
        <span className="material-symbols-outlined text-[18px] group-hover:rotate-180 transition-transform">
          expand_more
        </span>
      </button>
      <div className="absolute top-full left-0 w-64 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50">
        <div className="glass-card rounded-lg p-2 shadow-md">
          {PRODUCTS.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              className="block px-4 py-3 rounded transition-colors hover:bg-primary/5 hover:text-primary"
            >
              <span className="font-semibold block">{p.label}</span>
              <span className="text-[12px] opacity-70">{p.desc}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [dark, setDark] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className={dark ? "dark" : ""}>
      <div className="bg-surface-bright dark:bg-surface-dark text-on-surface dark:text-surface-bright antialiased overflow-x-hidden min-h-screen flex flex-col relative font-body-md text-body-md">
        <div className="fixed top-0 left-1/4 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none -z-10" />
        <div className="fixed bottom-0 right-1/4 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none -z-10" />
        <div className="fixed inset-0 bg-grid-pattern pointer-events-none -z-10 opacity-50 dark:opacity-[0.07]" />

        <header className="sticky top-0 w-full z-50 flex justify-between items-center px-gutter py-4 max-w-container-max mx-auto bg-surface-card-light dark:bg-surface-card-dark backdrop-blur-lg border-b border-glass shadow-sm">
          <Link to="/" className="flex items-center" aria-label="Cybermon home">
            <img src={logo} alt="Cybermon" className="h-9 md:h-10 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex gap-8 items-center">
            <ProductsDropdown />
            <NavLink
              to="/resources"
              className={({ isActive }) =>
                `font-body-md text-body-md transition-colors py-2 ${
                  isActive
                    ? "text-primary font-semibold"
                    : "text-on-surface-variant dark:text-surface-variant hover:text-primary dark:hover:text-primary-fixed-dim"
                }`
              }
            >
              Resources
            </NavLink>
            <NavLink
              to="/blog"
              className={({ isActive }) =>
                `font-body-md text-body-md transition-colors py-2 ${
                  isActive
                    ? "text-primary font-semibold"
                    : "text-on-surface-variant dark:text-surface-variant hover:text-primary dark:hover:text-primary-fixed-dim"
                }`
              }
            >
              Blog
            </NavLink>
            <NavLink
              to="/free-scan"
              className={({ isActive }) =>
                `font-body-md text-body-md transition-colors py-2 ${
                  isActive
                    ? "text-primary font-semibold"
                    : "text-on-surface-variant dark:text-surface-variant hover:text-primary dark:hover:text-primary-fixed-dim"
                }`
              }
            >
              Free Scan
            </NavLink>
            <a
              href="mailto:customer-services@cybermon.co.in"
              className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary dark:hover:text-primary-fixed-dim transition-colors py-2"
            >
              Contact Us
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setDark(!dark)}
              aria-label="Toggle dark mode"
              className="border border-glass rounded-DEFAULT p-2 text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-[18px] block">
                {dark ? "light_mode" : "dark_mode"}
              </span>
            </button>
            <a
              href={BOOK_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex bg-primary-container text-on-primary-container font-headline-card text-[14px] px-6 py-2 rounded-DEFAULT font-semibold hover:scale-105 transition-transform shadow-sm"
            >
              Book Demo
            </a>
            <button
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              className="md:hidden border border-glass rounded-DEFAULT p-2 text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-[20px] block">
                {mobileMenuOpen ? "close" : "menu"}
              </span>
            </button>
          </div>
        </header>

        {mobileMenuOpen && (
          <div className="md:hidden relative z-40 w-full bg-surface-card-light dark:bg-surface-card-dark backdrop-blur-lg border-b border-glass shadow-sm">
            <nav className="flex flex-col px-gutter py-4 gap-1">
              <span className="font-label-sm text-label-sm text-on-surface-variant dark:text-surface-variant uppercase tracking-widest mt-2 mb-1">
                Products
              </span>
              {PRODUCTS.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-body-md text-body-md text-on-surface dark:text-surface-bright hover:text-primary transition-colors py-2"
                >
                  {p.label}
                </Link>
              ))}
              <div className="border-t border-glass my-2" />
              <Link
                to="/resources"
                onClick={() => setMobileMenuOpen(false)}
                className="font-body-md text-body-md text-on-surface dark:text-surface-bright hover:text-primary transition-colors py-2"
              >
                Resources
              </Link>
              <Link
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className="font-body-md text-body-md text-on-surface dark:text-surface-bright hover:text-primary transition-colors py-2"
              >
                Blog
              </Link>
              <Link
                to="/free-scan"
                onClick={() => setMobileMenuOpen(false)}
                className="font-body-md text-body-md text-on-surface dark:text-surface-bright hover:text-primary transition-colors py-2"
              >
                Free Scan
              </Link>
              <a
                href="mailto:customer-services@cybermon.co.in"
                onClick={() => setMobileMenuOpen(false)}
                className="font-body-md text-body-md text-on-surface dark:text-surface-bright hover:text-primary transition-colors py-2"
              >
                Contact Us
              </a>
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="mt-3 inline-flex justify-center items-center bg-primary-container text-on-primary-container font-headline-card text-[14px] px-6 py-3 rounded-DEFAULT font-semibold shadow-sm"
              >
                Book Demo
              </a>
            </nav>
          </div>
        )}

        <Outlet />

        <footer className="w-full px-gutter py-section-gap flex flex-col md:flex-row justify-between items-start max-w-container-max mx-auto bg-surface-bright dark:bg-surface-dark border-t border-glass relative">
          <div className="flex flex-col gap-4 mb-8 md:mb-0">
            <Link to="/" className="flex items-center">
              <img src={logo} alt="Cybermon" className="h-11 w-auto object-contain" />
            </Link>
            <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant max-w-xs">
              AI-Driven Cyber Risk Intelligence Platform.
            </p>
          </div>

          <div className="flex flex-col gap-8 md:flex-row md:gap-16">
            <nav className="flex flex-col gap-3">
              <span className="font-label-sm text-label-sm text-on-surface dark:text-surface-bright font-semibold uppercase tracking-widest mb-2 block">
                Platform
              </span>
              <Link to="/asm" className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                Attack Surface
              </Link>
              <Link to="/dri" className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                Digital Risk
              </Link>
              <Link to="/compliance" className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                Compliance
              </Link>
              <Link to="/resources" className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                Resources
              </Link>
              <Link to="/blog" className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                Blog
              </Link>
              <Link to="/free-scan" className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                Free Scan
              </Link>
            </nav>
            <nav className="flex flex-col gap-3">
              <span className="font-label-sm text-label-sm text-on-surface dark:text-surface-bright font-semibold uppercase tracking-widest mb-2 block">
                Company
              </span>
              <a href="mailto:customer-services@cybermon.co.in" className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                Contact
              </a>
              <a href={`tel:${CONTACT_PHONE_TEL}`} className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors">
                {CONTACT_PHONE}
              </a>
            </nav>
          </div>

          <div className="w-full flex justify-center mt-12 md:hidden">
            <span className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant text-sm">
              © 2026 Cybermon Intelligence. All rights reserved.
            </span>
          </div>
          <div className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2">
            <span className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant text-sm">
              © 2026 Cybermon Intelligence. All rights reserved.
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
